import { useCallback, useState } from 'react';
import { LayoutGroup, motion } from 'framer-motion';
import { DragOutlined } from '@ant-design/icons';
import { DndContext, DragOverlay, PointerSensor, useDroppable, useDraggable, useSensor, useSensors } from '@dnd-kit/core';
import { Container } from '@/components/ui/Container';
import {
    SprintKanbanScrollChainStyle,
    SprintKanbanDragCursorStyle,
} from '@/components/module/projects/sprints_tab/SprintKanbanBoard';
import PMOSubmissionGroupCard from '@/components/module/projects/pmo_tab/PMOSubmissionGroupCard';
import { getSprintTaskApiId } from '@hooks/projects/projectHelpers';
import { useSprintKanbanSyncHorizontalScroll } from '@hooks/projects/useProjectSprints';
import { useMatchMedia } from '@hooks/useMatchMedia';
import {
    SprintKanbanFill,
    KanbanThreeColumnRow,
    KanbanColumnPanel,
    KanbanColumnHeader,
    KanbanColumnTitle,
    KanbanColumnCountBadge,
    TaskCardKanbanStatusPillCount,
    KanbanColumnBody,
    KanbanDragDropPlaceholder,
    kanbanDesktopHeaderCellFlexStyle,
    kanbanDesktopBoardRowInnerStyle,
    kanbanDesktopBoardHeaderRowInnerStyle,
} from '@/pages/inside/tenant/style';
import { COL_PMO } from '@hooks/projects/useProjectPMO';

/** Visual column badge variants — aligned with Blockers (todo | completed). */
const COL = { SUBMISSION: 'todo', SUBMITTED_CLIENT: 'completed' };

const DROP_MESSAGES = {
    [COL_PMO.SUBMISSION]: 'Drop here — Submission',
    [COL_PMO.SUBMITTED_CLIENT]: 'Drop here to submit to client',
};

const KANBAN_LAYOUT_DURATION = 0.52;
const KANBAN_LAYOUT_EASE = [0.22, 1, 0.36, 1];

const SPRINT_KANBAN_DRAG_CLASS = 'sprint-kanban-dragging';

/**
 * PMO tab: two columns — pending submission (draggable) | submitted to client (read-only).
 * Layout, scroll chain, and drag chrome match {@link BlockersKanbanBoard} / Sprints Kanban.
 * The “Submitted to Client” column uses `onOpenSubmittedClientSubmission` (read-only detail) and `onTaskFromSubmittedClient` (task detail).
 */
export default function PMOKanbanBoard({
    submissionRows,
    submittedToClientRows,
    submissionDragEnabled = true,
    onSubmissionOpen,
    onTaskFromSubmission,
    /** Header click on “Submitted to Client” — read-only submission detail (notes, tasks). */
    onOpenSubmittedClientSubmission,
    /** Task rows in “Submitted to Client” — open task detail without submit modal. */
    onTaskFromSubmittedClient,
    dropTarget,
    draggingColumn,
    onDndDragStart,
    onDndDragOverColumn,
    onDndDragEnd,
}) {
    const isNarrow = useMatchMedia('(max-width: 1024px)');
    const { headerScrollRef, bodyScrollRef, onHeaderScroll, onBodyScroll } = useSprintKanbanSyncHorizontalScroll();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
    );

    const [activeDragPayload, setActiveDragPayload] = useState(null);

    const handleDragStart = useCallback(
        (event) => {
            const payload = event?.active?.data?.current ?? null;
            setActiveDragPayload(payload);
            onDndDragStart?.(payload);
            document.body.classList.add(SPRINT_KANBAN_DRAG_CLASS);
        },
        [onDndDragStart],
    );

    const handleDragOver = useCallback(
        (event) => {
            const overColumnId = event?.over?.id ?? null;
            const payload = event?.active?.data?.current ?? null;
            if (overColumnId) onDndDragOverColumn?.(String(overColumnId), payload);
        },
        [onDndDragOverColumn],
    );

    const handleDragEnd = useCallback(
        (event) => {
            const overColumnId = event?.over?.id ?? null;
            const payload = event?.active?.data?.current ?? null;
            onDndDragEnd?.({ overColumnId: overColumnId != null ? String(overColumnId) : null, payload });
            setActiveDragPayload(null);
            document.body.classList.remove(SPRINT_KANBAN_DRAG_CLASS);
        },
        [onDndDragEnd],
    );

    const handleDragCancel = useCallback(() => {
        setActiveDragPayload(null);
        document.body.classList.remove(SPRINT_KANBAN_DRAG_CLASS);
    }, []);

    function DroppableKanbanColumnPanel({ columnId, children, ...panelProps }) {
        const { setNodeRef } = useDroppable({ id: columnId });
        return (
            <KanbanColumnPanel ref={setNodeRef} {...panelProps}>
                {children}
            </KanbanColumnPanel>
        );
    }

    function DraggableSubmissionCard({ row, children }) {
        const first = row?.taskEntries?.[0];
        const sprint = first?.sprint;
        const task = first?.task;
        const sprintId = sprint?.id;
        const taskId = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
        const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
            id: `pmo-submission-${String(row?.key ?? row?.submission?.id ?? taskId ?? 'unknown')}`,
            data: {
                sprintId,
                taskId,
                sourceColumn: COL_PMO.SUBMISSION,
                submissionId: row?.submission?.id,
            },
            disabled: !submissionDragEnabled,
        });
        const style = transform
            ? {
                transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
            }
            : undefined;
        return (
            <div
                ref={setNodeRef}
                style={{ ...style, opacity: isDragging ? 0.55 : 1, width: '100%', minWidth: 0 }}
                {...attributes}
                {...listeners}
            >
                {children}
            </div>
        );
    }

    const showDragPlaceholder = (columnId) => Boolean(draggingColumn && draggingColumn !== columnId);

    const dropZoneActive = (columnId) =>
        Boolean(draggingColumn && draggingColumn !== columnId && dropTarget === columnId);

    const renderSubmissionCard = (row) => {
        const { submission, taskEntries } = row;
        const first = taskEntries?.[0];
        const sprint = first?.sprint;
        const task = first?.task;
        const sprintId = sprint?.id;
        const apiId = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
        const listKey = row.key;
        const layoutId = `pmo-kanban-submission-${String(listKey)}`;
        const card = (
            <motion.div
                layout
                layoutId={layoutId}
                style={{ minHeight: 0 }}
                transition={{
                    layout: {
                        duration: KANBAN_LAYOUT_DURATION,
                        ease: KANBAN_LAYOUT_EASE,
                    },
                }}
            >
                <PMOSubmissionGroupCard
                    sidLabel={row.sidLabel}
                    submissionTitle={row.submissionTitle}
                    notesPreview={row.notesPreview}
                    sections={row.cardSections}
                    taskEntries={taskEntries}
                    isQAPassed={row.submission?.isQAPassed}
                    onOpenSubmission={() => onSubmissionOpen?.(submission)}
                    onTaskClick={(entry) => onTaskFromSubmission?.(submission, entry)}
                />
            </motion.div>
        );
        return (
            <div key={listKey} style={{ width: '100%', flexShrink: 0, minWidth: 0 }}>
                <DraggableSubmissionCard row={row}>{card}</DraggableSubmissionCard>
            </div>
        );
    };

    const renderClientCard = (row) => {
        const { submission, taskEntries } = row;
        const key = row.key;
        const layoutId = `pmo-kanban-client-${String(key)}`;
        return (
            <div key={key} style={{ width: '100%', flexShrink: 0, minWidth: 0 }}>
                <motion.div
                    layout
                    layoutId={layoutId}
                    style={{ minHeight: 0 }}
                    transition={{
                        layout: {
                            duration: KANBAN_LAYOUT_DURATION,
                            ease: KANBAN_LAYOUT_EASE,
                        },
                    }}
                >
                    <PMOSubmissionGroupCard
                        sidLabel={row.sidLabel}
                        submissionTitle={row.submissionTitle}
                        notesPreview={row.notesPreview}
                        sections={row.cardSections}
                        taskEntries={taskEntries}
                        isQAPassed={row.submission?.isQAPassed}
                        viewSubmittedHeader
                        onOpenSubmission={() => onOpenSubmittedClientSubmission?.(submission)}
                        onTaskClick={(entry) => onTaskFromSubmittedClient?.(submission, entry)}
                    />
                </motion.div>
            </div>
        );
    };

    const kanbanColumns = isNarrow ? (
        <KanbanThreeColumnRow className="sprint-kanban-column-row-natural" style={{ marginTop: 0 }}>
            <DroppableKanbanColumnPanel columnId={COL_PMO.SUBMISSION}>
                <KanbanColumnHeader>
                    <KanbanColumnTitle>Submission</KanbanColumnTitle>
                    <KanbanColumnCountBadge $variant={COL.SUBMISSION}>{submissionRows.length}</KanbanColumnCountBadge>
                </KanbanColumnHeader>
                <KanbanColumnBody
                    className="sprint-kanban-column-body-natural"
                    $qaStack
                    data-drag-placeholder={showDragPlaceholder(COL_PMO.SUBMISSION) ? 'true' : undefined}
                    $dragPlaceholder={showDragPlaceholder(COL_PMO.SUBMISSION)}
                >
                    {showDragPlaceholder(COL_PMO.SUBMISSION) ? (
                        <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL_PMO.SUBMISSION)}>
                            <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                            <span>{DROP_MESSAGES[COL_PMO.SUBMISSION]}</span>
                        </KanbanDragDropPlaceholder>
                    ) : (
                        submissionRows.map((row) => renderSubmissionCard(row))
                    )}
                </KanbanColumnBody>
            </DroppableKanbanColumnPanel>

            <DroppableKanbanColumnPanel columnId={COL_PMO.SUBMITTED_CLIENT}>
                <KanbanColumnHeader>
                    <KanbanColumnTitle>Submitted to Client</KanbanColumnTitle>
                    <TaskCardKanbanStatusPillCount
                        as="span"
                        aria-label={`${submittedToClientRows.length} submitted to client`}
                    >
                        {submittedToClientRows.length}
                    </TaskCardKanbanStatusPillCount>
                </KanbanColumnHeader>
                <KanbanColumnBody
                    className="sprint-kanban-column-body-natural"
                    $qaStack
                    data-drag-placeholder={showDragPlaceholder(COL_PMO.SUBMITTED_CLIENT) ? 'true' : undefined}
                    $dragPlaceholder={showDragPlaceholder(COL_PMO.SUBMITTED_CLIENT)}
                >
                    {showDragPlaceholder(COL_PMO.SUBMITTED_CLIENT) ? (
                        <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL_PMO.SUBMITTED_CLIENT)}>
                            <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                            <span>{DROP_MESSAGES[COL_PMO.SUBMITTED_CLIENT]}</span>
                        </KanbanDragDropPlaceholder>
                    ) : (
                        submittedToClientRows.map((row) => renderClientCard(row))
                    )}
                </KanbanColumnBody>
            </DroppableKanbanColumnPanel>
        </KanbanThreeColumnRow>
    ) : (
        <div className="sprint-kanban-desktop-split">
            <div data-sprint-kanban-sticky-strip className="sprint-kanban-sticky-strip">
                <div ref={headerScrollRef} className="sprint-kanban-h-scroll-sync" onScroll={onHeaderScroll}>
                    <div style={kanbanDesktopBoardHeaderRowInnerStyle}>
                        <div style={kanbanDesktopHeaderCellFlexStyle}>
                            <KanbanColumnHeader>
                                <KanbanColumnTitle>Submission</KanbanColumnTitle>
                                <KanbanColumnCountBadge $variant={COL.SUBMISSION}>{submissionRows.length}</KanbanColumnCountBadge>
                            </KanbanColumnHeader>
                        </div>
                        <div style={kanbanDesktopHeaderCellFlexStyle}>
                            <KanbanColumnHeader>
                                <KanbanColumnTitle>Submitted to Client</KanbanColumnTitle>
                                <TaskCardKanbanStatusPillCount
                                    as="span"
                                    aria-label={`${submittedToClientRows.length} submitted to client`}
                                >
                                    {submittedToClientRows.length}
                                </TaskCardKanbanStatusPillCount>
                            </KanbanColumnHeader>
                        </div>
                    </div>
                </div>
            </div>
            <div className="sprint-kanban-desktop-body-wrap">
                <div ref={bodyScrollRef} className="sprint-kanban-h-scroll-sync" onScroll={onBodyScroll}>
                    <div className="sprint-kanban-desktop-board-row" style={kanbanDesktopBoardRowInnerStyle}>
                        <DroppableKanbanColumnPanel columnId={COL_PMO.SUBMISSION}>
                            <KanbanColumnBody
                                className="sprint-kanban-column-body-natural"
                                $qaStack
                                data-drag-placeholder={showDragPlaceholder(COL_PMO.SUBMISSION) ? 'true' : undefined}
                                $dragPlaceholder={showDragPlaceholder(COL_PMO.SUBMISSION)}
                            >
                                {showDragPlaceholder(COL_PMO.SUBMISSION) ? (
                                    <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL_PMO.SUBMISSION)}>
                                        <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                        <span>{DROP_MESSAGES[COL_PMO.SUBMISSION]}</span>
                                    </KanbanDragDropPlaceholder>
                                ) : (
                                    submissionRows.map((row) => renderSubmissionCard(row))
                                )}
                            </KanbanColumnBody>
                        </DroppableKanbanColumnPanel>

                        <DroppableKanbanColumnPanel columnId={COL_PMO.SUBMITTED_CLIENT}>
                            <KanbanColumnBody
                                className="sprint-kanban-column-body-natural"
                                $qaStack
                                data-drag-placeholder={showDragPlaceholder(COL_PMO.SUBMITTED_CLIENT) ? 'true' : undefined}
                                $dragPlaceholder={showDragPlaceholder(COL_PMO.SUBMITTED_CLIENT)}
                            >
                                {showDragPlaceholder(COL_PMO.SUBMITTED_CLIENT) ? (
                                    <KanbanDragDropPlaceholder
                                        role="status"
                                        $active={dropZoneActive(COL_PMO.SUBMITTED_CLIENT)}
                                    >
                                        <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                        <span>{DROP_MESSAGES[COL_PMO.SUBMITTED_CLIENT]}</span>
                                    </KanbanDragDropPlaceholder>
                                ) : (
                                    submittedToClientRows.map((row) => renderClientCard(row))
                                )}
                            </KanbanColumnBody>
                        </DroppableKanbanColumnPanel>
                    </div>
                </div>
            </div>
        </div>
    );

    const kanbanRow = (
        <SprintKanbanFill className="sprint-kanban-fill-natural">
            <LayoutGroup id="pmo-kanban-board">{kanbanColumns}</LayoutGroup>
        </SprintKanbanFill>
    );

    return (
        <Container
            className="pmo-kanban-root sprint-kanban-root"
            $display="flex"
            $flexDirection="column"
            $flex="1 1 0%"
            $minHeight="0"
            $gap="0"
            $overflow="hidden"
            style={{ width: '100%' }}
        >
            <SprintKanbanDragCursorStyle />
            <SprintKanbanScrollChainStyle />
            <Container
                $display="flex"
                $flexDirection="column"
                $flex="1"
                $minHeight="0"
                $overflow="hidden"
                style={{ width: '100%' }}
            >
                <DndContext
                    sensors={sensors}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    {kanbanRow}
                    <DragOverlay>
                        {activeDragPayload?.submissionId ? (
                            <div style={{ width: 420, maxWidth: '92vw' }}>
                                <PMOSubmissionGroupCard
                                    sidLabel={
                                        submissionRows.find((r) => String(r?.submission?.id) === String(activeDragPayload.submissionId))
                                            ?.sidLabel
                                    }
                                    submissionTitle={
                                        submissionRows.find((r) => String(r?.submission?.id) === String(activeDragPayload.submissionId))
                                            ?.submissionTitle
                                    }
                                    notesPreview={
                                        submissionRows.find((r) => String(r?.submission?.id) === String(activeDragPayload.submissionId))
                                            ?.notesPreview
                                    }
                                    sections={
                                        submissionRows.find((r) => String(r?.submission?.id) === String(activeDragPayload.submissionId))
                                            ?.cardSections
                                    }
                                    taskEntries={
                                        submissionRows.find((r) => String(r?.submission?.id) === String(activeDragPayload.submissionId))
                                            ?.taskEntries ?? []
                                    }
                                />
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            </Container>
        </Container>
    );
}
